import { useEffect, type ReactNode } from 'react';
import { X } from 'lucide-react';

interface Props {
  open: boolean;
  onClose: () => void;
  title: string;
  children: ReactNode;
  /** Buttons row at the bottom (stays visible while the body scrolls). */
  footer?: ReactNode;
  size?: 'md' | 'lg';
}

/** DaisyUI modal shell used by admin create/edit dialogs. Escape and backdrop click close it. */
export function AdminDialog({ open, onClose, title, children, footer, size = 'md' }: Props) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;
  return (
    <div className="modal modal-open" onClick={onClose}>
      <div
        className={`modal-box p-0 flex flex-col max-h-[90vh] ${size === 'lg' ? 'max-w-3xl' : 'max-w-xl'}`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-5 py-4 border-b border-neutral-200 dark:border-neutral-700">
          <h3 className="font-semibold text-lg dark:text-neutral-100">{title}</h3>
          <button type="button" onClick={onClose} className="p-1 text-neutral-500 hover:text-neutral-800 dark:hover:text-neutral-200" aria-label="Close">
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto px-5 py-4">{children}</div>
        {footer && (
          <div className="flex items-center justify-end gap-2 px-5 py-3 border-t border-neutral-200 dark:border-neutral-700">
            {footer}
          </div>
        )}
      </div>
    </div>
  );
}
